import { referenceEqual } from "./equality";
import type { MemorySliceDescriptor } from "./types";

/**
 * In-memory cache entry for one slice key.
 *
 * @typeParam TValue Slice value type.
 */
type MemoryEntry<TValue = unknown> = {

    /** Current value served to React snapshots. */
    "value": TValue;

    /** `useSyncExternalStore` listeners notified on value changes. */
    "listeners": Set<() => void>;
};

/**
 * Generic in-memory slice store: snapshots, subscriptions, and updates without I/O.
 */
export class MemorySliceStore {

    private readonly entries = new Map<string, MemoryEntry>();

    /**
     * Returns an existing cache entry or seeds defaults for a new slice key.
     *
     * @typeParam TValue Slice value type.
     * @param slice Slice descriptor.
     * @returns Mutable cache entry for the slice key.
     */
    private getOrCreateEntry<TValue>( slice: MemorySliceDescriptor<TValue> ): MemoryEntry<TValue> {
        let entry = this.entries.get( slice.key ) as MemoryEntry<TValue> | undefined;
        if ( entry === undefined ) {
            entry = {
                "value":     slice.default(),
                "listeners": new Set(),
            };
            this.entries.set( slice.key, entry as MemoryEntry );
        }

        return entry;
    }

    /**
     * Notifies all subscribers that the slice value may have changed.
     *
     * @param sliceKey Slice key.
     */
    private notify( sliceKey: string ): void {
        const entry = this.entries.get( sliceKey );
        if ( entry === undefined ) {
            return;
        }

        for ( const listener of entry.listeners ) {
            listener();
        }
    }

    /**
     * Returns the current in-memory snapshot for `useSyncExternalStore`.
     *
     * @typeParam TValue Slice value type.
     * @param slice Slice descriptor.
     * @returns Current cached value (defaults when the entry does not exist).
     */
    getSnapshot<TValue>( slice: MemorySliceDescriptor<TValue> ): TValue {
        return this.getOrCreateEntry( slice ).value;
    }

    /**
     * Peeks at the current value without creating a subscription entry.
     *
     * @typeParam TValue Slice value type.
     * @param slice Slice descriptor.
     * @returns Cached value or `slice.default()` when the entry does not exist.
     */
    peek<TValue>( slice: MemorySliceDescriptor<TValue> ): TValue {
        const entry = this.entries.get( slice.key ) as MemoryEntry<TValue> | undefined;
        if ( entry === undefined ) {
            return slice.default();
        }

        return entry.value;
    }

    /**
     * Subscribes to slice updates for `useSyncExternalStore`.
     *
     * @typeParam TValue Slice value type.
     * @param slice Slice descriptor (seeds defaults when the entry is first created).
     * @param listener Change listener invoked after value updates.
     * @returns Unsubscribe function.
     */
    subscribe<TValue>( slice: MemorySliceDescriptor<TValue>, listener: () => void ): () => void {
        const entry = this.getOrCreateEntry( slice );
        entry.listeners.add( listener );

        return () => {
            entry.listeners.delete( listener );
        };
    }

    /**
     * Replaces the slice value and notifies subscribers when the reference changes.
     *
     * @typeParam TValue Slice value type.
     * @param slice Slice descriptor.
     * @param value New value.
     */
    replaceValue<TValue>( slice: MemorySliceDescriptor<TValue>, value: TValue ): void {
        const entry = this.getOrCreateEntry( slice );
        if ( referenceEqual( entry.value, value ) ) {
            return;
        }

        entry.value = value;
        this.notify( slice.key );
    }

    /**
     * Updates a slice value and notifies subscribers when the resolved value changes.
     *
     * @typeParam TValue Slice value type.
     * @param slice Slice descriptor.
     * @param next New value or functional updater (same semantics as `useState`).
     */
    setValue<TValue>(
        slice: MemorySliceDescriptor<TValue>,
        next: TValue | ( ( previous: TValue ) => TValue ),
    ): void {
        const entry = this.getOrCreateEntry( slice );
        const resolved = typeof next === "function"
            ? ( next as ( previous: TValue ) => TValue )( entry.value )
            : next;

        if ( referenceEqual( entry.value, resolved ) ) {
            return;
        }

        entry.value = resolved;
        this.notify( slice.key );
    }

    /**
     * Resets in-memory state.
     *
     * @remarks Intended for unit tests only.
     */
    resetForTests(): void {
        this.entries.clear();
    }

}
